import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Gift, Image, PaperPlaneTilt } from '@phosphor-icons/react'
import { useAnalytics } from '../context/AnalyticsContext'
import { useAuth } from '../context/AuthContext'
import { useReadChats } from '../context/ReadChatsContext'
import { loadMessages, sendMessage, loadChats, getOrCreateChat, markMessageAsRead } from '../lib/chats'
import { supabase } from '../lib/supabase'
import { loadProfileForModal } from '../lib/loadProfileForModal'
import { saveLike, saveDislike, checkDailySuperLikeLimit, getSuperLikeResetTimeString, incrementDailySuperLikeCount } from '../lib/explore'
import { uploadChatPhoto } from '../lib/storage'
import { getGiftByName, getGiftImageUrl } from '../lib/gifts'
import ProfileModal from '../components/ProfileModal'
import CompatibilityModal from '../components/CompatibilityModal'
import GiftModal from '../components/GiftModal'
import Loader from '../components/Loader'
import './ChatDetail.css'

const NETLIFY_FUNCTIONS = import.meta.env.VITE_NETLIFY_FUNCTIONS_URL || ''

const ChatDetail = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { chatId, otherUserId: otherUserIdFromRoute } = useParams()
  const location = useLocation()
  const isNewChat = !!otherUserIdFromRoute
  const effectiveChatId = isNewChat ? null : chatId
  const { trackEvent } = useAnalytics()
  const { user, profile } = useAuth()
  const hasSubscription = profile?.subscription_status === 'active' || profile?.subscription_status === 'trial'
  const isFemale = profile?.gender === 'female'
  const { markChatAsRead } = useReadChats()
  
  const [messages, setMessages] = useState([])
  const [chatInfo, setChatInfo] = useState(null)
  const [messageText, setMessageText] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [profileModalUser, setProfileModalUser] = useState(null)
  const [showCompatibilityModal, setShowCompatibilityModal] = useState(false)
  const [compatibilityProfile, setCompatibilityProfile] = useState(null)
  const [showGiftModal, setShowGiftModal] = useState(false)
  const [giftError, setGiftError] = useState('')
  const messagesEndRef = useRef(null)
  const messageInputRef = useRef(null)
  const photoInputRef = useRef(null)
  const lastChatIdRef = useRef(null)
  const initialScrollDoneRef = useRef(false)
  const giftSuccessTrackedRef = useRef(false)
  /** Уже показывали этот чат — повторный loadChatData без полноэкранного Loader (вкладка/рефреш сессии) */
  const loadedChatIdRef = useRef(null)

  useEffect(() => {
    if (isNewChat && user && otherUserIdFromRoute) {
      loadNewChatPartner(otherUserIdFromRoute)
    } else if (user && effectiveChatId) {
      loadChatData()
      const unsubscribe = subscribeToMessages()
      return () => { if (unsubscribe) unsubscribe() }
    }
    // user?.id — при TOKEN_REFRESHED приходит новый объект user; иначе эффект гонял loadChatData и Loader снова
  }, [user?.id, effectiveChatId, isNewChat, otherUserIdFromRoute])

  useEffect(() => {
    if (effectiveChatId !== lastChatIdRef.current) {
      lastChatIdRef.current = effectiveChatId
      initialScrollDoneRef.current = false
    }
  }, [effectiveChatId])

  const scrollToBottom = useCallback((instant = false) => {
    const el = messagesEndRef.current
    if (!el) return
    el.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'end' })
  }, [])

  useEffect(() => {
    const isInitialOpen = !initialScrollDoneRef.current && messages.length > 0
    if (isInitialOpen) {
      initialScrollDoneRef.current = true
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToBottom(true))
      })
    } else if (messages.length > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToBottom(false))
      })
    }
  }, [messages, scrollToBottom])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('gift_success') !== '1' || !user || !effectiveChatId) return
    if (giftSuccessTrackedRef.current) return
    giftSuccessTrackedRef.current = true
    trackEvent('gift_checkout_success', { chatId: effectiveChatId })
    loadMessages(effectiveChatId, user.id).then(setMessages)
    navigate(location.pathname, { replace: true })
  }, [location.search, location.pathname, user?.id, effectiveChatId, navigate, trackEvent])

  const loadNewChatPartner = async (otherUserId) => {
    if (!user) return
    setLoading(true)
    try {
      const profileData = await loadProfileForModal(otherUserId)
      if (profileData) {
        setChatInfo({
          id: null,
          matchId: null,
          otherUserId: profileData.id,
          otherUserName: profileData.name,
          otherUserPhoto: profileData.photos?.[0] || null,
          otherUserGender: profileData.gender || null
        })
      }
      setMessages([])
    } catch (e) {
      console.error('[ChatDetail] loadNewChatPartner', e)
    } finally {
      setLoading(false)
    }
  }

  const loadChatData = async () => {
    if (!user || !effectiveChatId) return

    const showFullLoader = loadedChatIdRef.current !== effectiveChatId
    if (showFullLoader) setLoading(true)
    try {
      const chats = await loadChats(user.id)
      const chat = chats.find(c => c.id === effectiveChatId)
      
      if (chat) {
        setChatInfo(chat)
      } else {
        // Если чат не найден в списке, загружаем напрямую из БД
        const { data: chatData } = await supabase
          .from('chats')
          .select('id, user1_id, user2_id, match_id')
          .eq('id', effectiveChatId)
          .single()
        
        if (chatData) {
          const otherUserId = chatData.user1_id === user.id ? chatData.user2_id : chatData.user1_id
          const { data: otherUserProfile } = await supabase
            .from('profiles')
            .select('id, name, photos, gender')
            .eq('id', otherUserId)
            .single()
          
          let otherUserPhoto = null
          if (otherUserProfile?.photos?.[0]) {
            const photo = otherUserProfile.photos[0]
            if (photo.startsWith('http://') || photo.startsWith('https://')) {
              otherUserPhoto = photo
            } else {
              const { data } = supabase.storage
                .from('photos')
                .getPublicUrl(photo)
              otherUserPhoto = data.publicUrl
            }
          }
          
          setChatInfo({
            id: chatData.id,
            matchId: chatData.match_id,
            otherUserId,
            otherUserName: otherUserProfile?.name || 'Unknown',
            otherUserPhoto,
            otherUserGender: otherUserProfile?.gender || null
          })
        }
      }

      const messagesList = await loadMessages(effectiveChatId, user.id)
      setMessages(messagesList)
      markChatAsRead(effectiveChatId)
    } catch (error) {
      console.error('[ChatDetail] Error loading chat data:', error)
    } finally {
      loadedChatIdRef.current = effectiveChatId
      setLoading(false)
    }
  }

  const subscribeToMessages = () => {
    if (!effectiveChatId) return null

    const channelName = `chat:${effectiveChatId}`
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${effectiveChatId}`
        },
        async (payload) => {
          const newMsg = payload?.new
          if (!newMsg) return
          console.log('[ChatDetail] New message received (realtime):', newMsg.id)
          
          // Если пользователь находится в этом чате и видит сообщение сразу - помечаем как прочитанное
          const isFromOtherUser = newMsg.sender_id !== user?.id
          if (isFromOtherUser && newMsg.id && !newMsg.is_read) {
            await markMessageAsRead(newMsg.id)
            markChatAsRead(effectiveChatId)
            // Обновляем сообщение в состоянии с is_read: true
            newMsg.is_read = true
          }
          
          setMessages(prev => {
            const id = newMsg.id
            if (id && prev.some(m => m.id === id)) return prev
            return [...prev, { ...newMsg, id: id || `realtime-${Date.now()}` }]
          })
          scrollToBottom()
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.warn('[ChatDetail] Realtime channel error. Ensure Supabase Dashboard → Database → Replication includes table "messages".')
        }
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }

  const handleSendMessage = async (e) => {
    e?.preventDefault()
    if (!messageText.trim() || sending || !user) return
    if (!effectiveChatId && !isNewChat) return
    if (isNewChat && !otherUserIdFromRoute) return

    const content = messageText.trim()
    let targetChatId = effectiveChatId

    if (isNewChat) {
      const { data: chat, error: chatError } = await getOrCreateChat(user.id, otherUserIdFromRoute, null)
      if (chatError || !chat) {
        console.error('[ChatDetail] Failed to create chat:', chatError)
        return
      }
      targetChatId = chat.id
    }

    const tempMessage = {
      id: `temp-${Date.now()}`,
      chat_id: targetChatId,
      sender_id: user.id,
      content: content,
      is_read: false,
      created_at: new Date().toISOString()
    }
    setMessages(prev => [...prev, tempMessage])
    setMessageText('')
    setSending(true)
    scrollToBottom()

    try {
      trackEvent('message_sent', { chatId: targetChatId })
      const { data, error } = await sendMessage(targetChatId, user.id, content)
      if (error) {
        setMessages(prev => prev.filter(m => m.id !== tempMessage.id))
        setMessageText(content)
      } else if (data) {
        setMessages(prev => prev.map(m => (m.id === tempMessage.id ? data : m)))
        if (isNewChat) {
          navigate(`/chat/${targetChatId}`, { replace: true })
        }
      }
    } catch (error) {
      console.error('[ChatDetail] Exception sending message:', error)
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id))
      setMessageText(content)
    } finally {
      setSending(false)
      // На Android/iOS не закрывать клавиатуру после отправки — возвращаем фокус в поле ввода
      requestAnimationFrame(() => {
        messageInputRef.current?.focus()
      })
    }
  }

  const handleHeaderUserClick = async () => {
    if (!chatInfo?.otherUserId) return
    const fullProfile = await loadProfileForModal(chatInfo.otherUserId)
    if (fullProfile) setProfileModalUser(fullProfile)
  }

  const handleSeeCompatibility = () => {
    if (!hasSubscription) {
      trackEvent('paywall_shown', { reason: 'compatibility_details' })
      navigate('/paywall?reason=compatibility_details', { state: { from: location.pathname } })
      return
    }
    if (profileModalUser) {
      setCompatibilityProfile(profileModalUser)
      setProfileModalUser(null)
      setShowCompatibilityModal(true)
    }
  }

  const handleProfileModalLike = async () => {
    if (!user || !profileModalUser) return
    await saveLike(user.id, profileModalUser.id, false)
    setProfileModalUser(null)
  }
  const handleProfileModalSuperLike = async () => {
    if (!user || !profileModalUser) return
    if (!hasSubscription && !isFemale) {
      trackEvent('paywall_shown', { reason: 'super_like_no_access' })
      navigate('/paywall?reason=super_like_no_access', { state: { from: location.pathname } })
      return
    }
    const timezone = profile?.timezone || 'UTC'
    const limitData = await checkDailySuperLikeLimit(user.id, timezone)
    if (!limitData.canSuperLike) {
      trackEvent('super_like_limit_reached', { from: 'chat' })
      const resetIn = getSuperLikeResetTimeString(timezone)
      alert(`Daily Super Like limit reached. It will reset ${resetIn}.`)
      return
    }
    await saveLike(user.id, profileModalUser.id, true)
    await incrementDailySuperLikeCount(user.id, timezone)
    setProfileModalUser(null)
  }
  const handleProfileModalDislike = async () => {
    if (!user || !profileModalUser) return
    await saveDislike(user.id, profileModalUser.id)
    setProfileModalUser(null)
  }

  const handleGiftSelect = async (gift) => {
    if (!user || !chatInfo) return
    setGiftError('')
    trackEvent('gift_selected', { giftId: gift.id, price: gift.price })
    const toUserId = chatInfo.otherUserId || otherUserIdFromRoute
    if (!toUserId) {
      setShowGiftModal(false)
      return
    }
    let targetChatId = effectiveChatId
    if (isNewChat) {
      const { data: chat, error: chatError } = await getOrCreateChat(user.id, toUserId, null)
      if (chatError || !chat) {
        setGiftError('Could not open chat.')
        setShowGiftModal(false)
        return
      }
      targetChatId = chat.id
    }
    if (!targetChatId) {
      setShowGiftModal(false)
      return
    }
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      const url = NETLIFY_FUNCTIONS ? `${NETLIFY_FUNCTIONS}/create-gift-checkout-session` : '/.netlify/functions/create-gift-checkout-session'
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          giftType: gift.name,
          giftPrice: gift.price,
          toUserId,
          chatId: targetChatId
        })
      })
      const data = await res.json().catch(() => ({}))
      if (data.url) {
        setShowGiftModal(false)
        window.location.href = data.url
        return
      }
      setGiftError(data.error || 'Could not start checkout. Please try again.')
      setShowGiftModal(false)
    } catch (e) {
      console.error('[ChatDetail] Gift checkout error', e)
      setGiftError('Something went wrong. Please try again.')
      setShowGiftModal(false)
    }
  }

  const handlePhotoButtonClick = () => {
    if (!hasSubscription && !isFemale) {
      trackEvent('paywall_shown', { reason: 'photo_in_chat' })
      // Funnel здесь: у женского профиля пейвол для фото не показывается (!isFemale) — на Paywall пользователь не попадёт
      trackEvent('funnel_paywall_photo_in_chat_viewed', { reason: 'photo_in_chat' })
      navigate('/paywall?reason=photo_in_chat', { state: { from: location.pathname } })
      return
    }
    photoInputRef.current?.click()
  }

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !user) return

    // Валидация типа файла
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, or WebP)')
      e.target.value = ''
      return
    }

    // Валидация размера файла (максимум 10MB)
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      alert('Image is too large. Maximum size is 10MB.')
      e.target.value = ''
      return
    }

    e.target.value = ''
    let targetChatId = effectiveChatId
    if (isNewChat) {
      const { data: chat, error: chatError } = await getOrCreateChat(user.id, otherUserIdFromRoute, null)
      if (chatError || !chat) return
      targetChatId = chat.id
    }
    const tempMessage = {
      id: `temp-photo-${Date.now()}`,
      chat_id: targetChatId,
      sender_id: user.id,
      content: null,
      photo_url: null,
      is_read: false,
      created_at: new Date().toISOString()
    }
    setMessages(prev => [...prev, tempMessage])
    setSending(true)
    scrollToBottom()
    try {
      const photoUrl = await uploadChatPhoto(file, user.id)
      tempMessage.photo_url = photoUrl
      setMessages(prev => prev.map(m => (m.id === tempMessage.id ? { ...tempMessage, photo_url: photoUrl } : m)))
      trackEvent('photo_sent', { chatId: targetChatId })
      const { data, error } = await sendMessage(targetChatId, user.id, '', photoUrl)
      if (error) {
        setMessages(prev => prev.filter(m => m.id !== tempMessage.id))
      } else if (data) {
        setMessages(prev => prev.map(m => (m.id === tempMessage.id ? data : m)))
        if (isNewChat) navigate(`/chat/${targetChatId}`, { replace: true })
      }
    } catch (err) {
      console.error('[ChatDetail] Photo upload/send failed:', err)
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id))
    } finally {
      setSending(false)
    }
  }

  const formatMessageTime = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  }

  if (loading) {
    return (
      <div className="chat-detail-screen">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <Loader text="Loading..." />
        </div>
      </div>
    )
  }

  if (!chatInfo && !loading) {
    return (
      <div className="chat-detail-screen">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '16px' }}>
          <div>{t('chat.chatNotFound')}</div>
          <button onClick={() => navigate('/app/chats')}>{t('chat.backToChats')}</button>
        </div>
      </div>
    )
  }

  return (
    <div className="chat-detail-screen">
      {/* Header */}
      <div className="chat-detail-header">
        <button className="chat-back-btn" onClick={() => navigate('/app/chats')}>
          ←
        </button>
        <button type="button" className="chat-header-user chat-header-user-btn" onClick={handleHeaderUserClick}>
          <div className="chat-header-avatar">
            {chatInfo.otherUserPhoto ? (
              <img src={chatInfo.otherUserPhoto} alt={chatInfo.otherUserName} />
            ) : (
              <div className="chat-header-avatar-placeholder">
                {chatInfo.otherUserName?.[0] || '?'}
              </div>
            )}
          </div>
          <span className="chat-header-name">{chatInfo.otherUserName}</span>
        </button>
      </div>

      {/* Messages: снизу вверх — первое (старое) внизу у инпута, новые появляются ниже, старые сдвигаются вверх */}
      <div className="chat-messages">
        <div ref={messagesEndRef} className="chat-messages-end-spacer" aria-hidden />
        {messages.length === 0 ? (
          <div className="chat-empty-messages">
            <p>{t('chats.startChat')}</p>
          </div>
        ) : (
          [...messages].reverse().map((message) => {
            const isOwn = message.sender_id === user.id
            return (
              <div
                key={message.id}
                className={`chat-message ${isOwn ? 'chat-message-own' : 'chat-message-other'}`}
              >
                <div className="chat-message-bubble">
                  {message.content && <p className="chat-message-text">{message.content}</p>}
                  {message.photo_url && (
                    isOwn || hasSubscription || isFemale ? (
                      <img src={message.photo_url} alt="Photo" className="chat-message-photo" />
                    ) : (
                      <button
                        type="button"
                        className="chat-message-photo-wrap chat-message-photo-blurred"
                        onClick={() => {
                          trackEvent('paywall_shown', { reason: 'view_photo_in_chat' })
                          trackEvent('funnel_paywall_view_photo_in_chat_viewed', { reason: 'view_photo_in_chat' })
                          navigate('/paywall?reason=view_photo_in_chat', { state: { from: location.pathname } })
                        }}
                      >
                        <img src={message.photo_url} alt="Photo" className="chat-message-photo" />
                        <span className="chat-message-photo-lock">{t('chat.unlockToView')}</span>
                      </button>
                    )
                  )}
                  {message.gift_type && (() => {
                    const gift = getGiftByName(message.gift_type)
                    const giftImageUrl = getGiftImageUrl(gift)
                    const giftName = gift?.name || message.gift_type
                    return (
                      <div className="chat-message-gift">
                        <img src={giftImageUrl} alt={giftName} className="chat-message-gift-image" />
                      </div>
                    )
                  })()}
                  <span className="chat-message-time">{formatMessageTime(message.created_at)}</span>
                </div>
              </div>
            )
          })
        )}
      </div>

      {giftError ? (
        <p className="chat-gift-error" role="alert">
          {giftError}
        </p>
      ) : null}
      {/* Input: gift | photo (left of input) | input | send */}
      <form className="chat-input-container" onSubmit={handleSendMessage}>
        <button
          type="button"
          className="chat-input-btn chat-gift-btn"
          onClick={() => {
            setGiftError('')
            setShowGiftModal(true)
            trackEvent('gift_button_clicked')
          }}
        >
          <Gift size={22} weight="regular" />
        </button>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          className="chat-photo-input-hidden"
          onChange={handlePhotoSelect}
          aria-hidden
        />
        <button
          type="button"
          className="chat-input-btn chat-photo-btn"
          onClick={handlePhotoButtonClick}
          disabled={sending}
        >
          <Image size={22} weight="regular" />
        </button>
        <input
          ref={messageInputRef}
          type="text"
          className="chat-input"
          placeholder={t('chat.typeMessage')}
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          disabled={sending}
        />
        <button
          type="submit"
          className="chat-input-btn chat-send-btn"
          disabled={!messageText.trim() || sending}
        >
          <PaperPlaneTilt size={22} weight="fill" />
        </button>
      </form>

      {showGiftModal && (
        <GiftModal
          onClose={() => setShowGiftModal(false)}
          onSelectGift={handleGiftSelect}
        />
      )}

      {profileModalUser && (
        <ProfileModal
          profile={profileModalUser}
          onClose={() => setProfileModalUser(null)}
          mode="chat-profile"
          hasSubscription={hasSubscription}
          isFemale={isFemale}
          onSeeCompatibility={handleSeeCompatibility}
        />
      )}

      {showCompatibilityModal && compatibilityProfile && (
        <CompatibilityModal
          profile={compatibilityProfile}
          currentProfile={profile || { id: user?.id, date_of_birth: null }}
          fullContent={hasSubscription}
          onClose={() => {
            setShowCompatibilityModal(false)
            setCompatibilityProfile(null)
          }}
        />
      )}
    </div>
  )
}

export default ChatDetail
